import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDistanceToNow } from "date-fns";
import LeadCard from "@/components/LeadCard";
import { Tables } from "@/integrations/supabase/types";
import { useToast } from "@/components/ui/use-toast";
import { Header } from "@/components/Header";

type Lead = Tables<"leads">;

const Dashboard = () => {
  const navigate = useNavigate();
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login");
      }
    };

    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        navigate("/login");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  // Set up real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel('leads-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'leads'
        },
        (payload) => {
          // Add the new lead to the cache immediately
          queryClient.setQueryData(['leads'], (oldData: Lead[] | undefined) => {
            const newLead = payload.new as Lead;
            if (!oldData) return [newLead];
            return [newLead, ...oldData];
          });
          
          // Show toast notification
          toast({
            title: "New Lead Received",
            description: `${payload.new.name} has submitted a lead.`,
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient, toast]);

  const { data: leads, isLoading, error } = useQuery({
    queryKey: ['leads'],
    queryFn: async () => {
      console.log("Fetching leads...");
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error("Error fetching leads:", error);
        throw error;
      }
      console.log("Fetched leads:", data);
      return data;
    },
  });

  if (error) {
    console.error("Error in leads query:", error);
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">Leads Dashboard</h1>

          {isLoading ? (
            <div className="text-center py-8">Loading leads...</div>
          ) : !leads?.length ? (
            <div className="text-center py-8 text-gray-600">No leads found</div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Time Since</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((lead) => (
                    <TableRow 
                      key={lead.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => setSelectedLead(lead)}
                    >
                      <TableCell className="font-medium">{lead.name}</TableCell>
                      <TableCell>{lead.email}</TableCell>
                      <TableCell>{lead.phone || '-'}</TableCell>
                      <TableCell className="capitalize">{lead.source.replace(/_/g, ' ')}</TableCell>
                      <TableCell className="capitalize">{lead.employment_status || '-'}</TableCell>
                      <TableCell>{new Date(lead.created_at).toLocaleString()}</TableCell>
                      <TableCell>{formatDistanceToNow(new Date(lead.created_at), { addSuffix: true })}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {selectedLead && (
            <LeadCard
              lead={selectedLead}
              isOpen={!!selectedLead}
              onClose={() => setSelectedLead(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
